import {StoresWidgetID, APP_DEFINITION_ID} from '@wix/wixstores-client-core';
import {
  BooleanStyleParam,
  ColorStyleParam,
  ComponentClientSpecMapEntry,
  EditorSDK,
  FontStyleParam,
  NumericStyleParam,
  StyleParam,
  ColorParam,
  TPAPublicDataValue,
  TPAPublicDataScope,
  ComponentRef,
} from '@wix/platform-editor-sdk';
import {ThankYouPageStyleParams} from '@wix/wixstores-client-core/dist/es/src/style-params';
import {EcomStyleParams} from '@wix/ecom-platform-sdk/dist/es/src/types';
import {IHttpClient} from '@wix/http-client';
import {mapEcomColorToSdkColor, migrateMiniCartSettings} from './MigrateSideCart';

export class StyleParams {
  constructor(
    private readonly sdk: EditorSDK,
    private readonly applicationId: number,
    private readonly httpClient?: IHttpClient
  ) {}

  public async setCheckoutStyleParam(type: StyleParam['type'], key: string, param: {value: any}) {
    const allComponents = await this.getAllApplicationComponents();
    const checkoutComponent = allComponents.find((comp) => comp.widgetId === StoresWidgetID.CHECKOUT);
    if (!checkoutComponent) {
      return;
    }
    const compRef = await this.sdk.document.components.getById('', {id: checkoutComponent.id});

    await this.sdk.tpa.setStyleParams('', {
      compRef,
      styleParams: [{type, key, param}],
    });
  }

  public async setThankYouPageStyleParams(styleParams?: EcomStyleParams) {
    if (!styleParams?.thankYouPage) {
      return;
    }

    const allComponents = await this.getAllApplicationComponents();
    const thankYouPageComponent = this.getComponent(allComponents, StoresWidgetID.THANK_YOU_PAGE);
    if (!thankYouPageComponent) {
      return;
    }
    const compRef = await this.sdk.document.components.getById('', {id: thankYouPageComponent.id});
    const colorStyleParams = mapColorParams(styleParams);
    const booleanStyleParams = mapBooleanParams(styleParams);
    const fontStyleParams = mapFontParams(styleParams);
    const numberStyleParams = mapNumberParams(styleParams);
    const combinedStyleParams = [...colorStyleParams, ...booleanStyleParams, ...fontStyleParams, ...numberStyleParams];

    if (!combinedStyleParams.length) {
      return;
    }

    await this.sdk.tpa.setStyleParams('', {
      compRef,
      styleParams: combinedStyleParams,
    });

    const textParams = mapTextParams(styleParams);
    await Promise.all(
      textParams.map((param) =>
        this.sdk.tpa.data.set('', {
          compRef,
          ...param,
        })
      )
    );
  }

  public async setSideCartStyleParams() {
    await migrateMiniCartSettings({
      getCompRef: (component: ComponentClientSpecMapEntry) =>
        this.sdk.document.components.getById('', {id: component.id}),
      getComponent: async (widgetId: string) => {
        const allComponents = await this.sdk.document.tpa.app.getAllComps('', APP_DEFINITION_ID);
        return this.getComponent(allComponents, widgetId);
      },
      getStyleParams: (compRef: ComponentRef) => this.sdk.tpa.getStyleParams('', {compRef}),
      getSiteTextPresets: (compRef: ComponentRef) => this.sdk.tpa.getSiteTextPresets('', {compRef}),
      setStyleParams: (ref: ComponentRef, styleParams: StyleParam[]) =>
        this.sdk.tpa.setStyleParams('', {compRef: ref, styleParams}),
      getTexts: (compRef: ComponentRef) => this.sdk.tpa.data.getAll('', {compRef}),
      setTexts: async (options: {compRef: ComponentRef; scope: TPAPublicDataScope; key: string; value: string}) => {
        await this.sdk.tpa.data.set('', options);
      },
      getAppSettingsByExternalId: async (externalId: string) => {
        const response = await this.httpClient.get(
          `https://editor.wix.com/_api/wix-ecommerce-storefront-web/api?o=getAppSettings&s=WixStoresWebClient&q=query,getAppSettings($externalId:String!){appSettings(externalId:$externalId){widgetSettings}}&v=%7B%22externalId%22%3A%22${externalId}%22%7D`
        );
        return (response?.data as any)?.data?.appSettings?.widgetSettings;
      },
      isMultilingualEnabled: () => this.sdk.language?.multilingual?.isEnabled(''),
    });
  }

  private getAllApplicationComponents(): Promise<ComponentClientSpecMapEntry[]> {
    return this.sdk.document.tpa.app.getAllCompsByApplicationId('', this.applicationId);
  }

  private getComponent(allComponents: ComponentClientSpecMapEntry[], componentId: string) {
    return allComponents.find((comp) => comp.widgetId === componentId);
  }
}

function getColorStyleParams(key: string, param: ColorParam): ColorStyleParam {
  return getStyleParams<ColorStyleParam>('color', key, mapEcomColorToSdkColor(param));
}

function getBooleanStyleParams(key: string, param: BooleanStyleParam['param']['value']): BooleanStyleParam {
  return getStyleParams<BooleanStyleParam>('boolean', key, {value: param});
}

function getFontStyleParams(key: string, param: FontStyleParam['param']['value']): FontStyleParam {
  return getStyleParams<FontStyleParam>('font', key, {value: {...param, fontStyleParam: true, preset: 'Custom'}});
}

function getNumberStyleParams(key: string, param: NumericStyleParam['param']['value']): NumericStyleParam {
  return getStyleParams<NumericStyleParam>('number', key, {value: param});
}

function getStyleParams<T1 extends StyleParam>(type: T1['type'], key: string, param: T1['param']) {
  return {
    type,
    key,
    param,
  };
}

export function mapColorParams(styleParams: EcomStyleParams): ColorStyleParam[] {
  const styleParamsMap = styleParams.thankYouPage?.colorParams;
  if (!styleParamsMap) {
    return [];
  }
  return Object.entries(styleParamsMap)
    .filter(([_, value]) => value?.value)
    .map(([key, value]) =>
      getColorStyleParams(
        ThankYouPageStyleParams.ColorParamNames[key as keyof (typeof ThankYouPageStyleParams)['ColorParamNames']],
        value
      )
    );
}

export function mapBooleanParams(styleParams: EcomStyleParams): BooleanStyleParam[] {
  const styleParamsMap = styleParams.thankYouPage?.booleanParams;
  if (!styleParamsMap) {
    return [];
  }
  return Object.entries(styleParamsMap)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) =>
      getBooleanStyleParams(
        ThankYouPageStyleParams.BooleanParamNames[key as keyof (typeof ThankYouPageStyleParams)['BooleanParamNames']],
        value
      )
    );
}

export function mapFontParams(styleParams: EcomStyleParams): FontStyleParam[] {
  const styleParamsMap = styleParams.thankYouPage?.fontParams;
  if (!styleParamsMap) {
    return [];
  }
  return Object.entries(styleParamsMap)
    .filter(([_, value]) => value?.family)
    .map(([key, value]) =>
      getFontStyleParams(
        ThankYouPageStyleParams.FontParamNames[key as keyof (typeof ThankYouPageStyleParams)['FontParamNames']],
        value
      )
    );
}

export function mapTextParams(
  styleParams: EcomStyleParams
): {key: string; value: TPAPublicDataValue; scope: TPAPublicDataScope}[] {
  const styleParamsMap = styleParams.thankYouPage?.textsParams;
  if (!styleParamsMap) {
    return [];
  }
  return Object.entries(styleParamsMap)
    .filter(([_, value]) => value)
    .map(([key, value]) => ({
      key: ThankYouPageStyleParams.TextParamNames[key as keyof (typeof ThankYouPageStyleParams)['TextParamNames']],
      value,
      scope: 'APP',
    }));
}

export function mapNumberParams(styleParams: EcomStyleParams): NumericStyleParam[] {
  const styleParamsMap = styleParams.thankYouPage?.numberParams;
  if (!styleParamsMap) {
    return [];
  }
  return Object.entries(styleParamsMap)
    .filter(([_, value]) => typeof value === 'number')
    .map(([key, value]) =>
      getNumberStyleParams(
        ThankYouPageStyleParams.NumberParamNames[key as keyof (typeof ThankYouPageStyleParams)['NumberParamNames']],
        value
      )
    );
}
